import { Link } from '@tanstack/react-router';
import { ListChecks, PauseCircle, Undo2, X } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import {
  CRITERION_MARK_LABELS,
  CRITERION_MARK_SYMBOLS,
  CV_SCAN_MESSAGE,
  EDUCATION_LEVEL_LABELS,
  type ProfileBreakdown,
  type RankedCriterion,
  type RankingDetail,
  type RankingRow,
  type ReviewStatus,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { Kbd } from '../../../components/Kbd';
import { SkeletonLines } from '../../../components/Skeleton';
import { Stars } from '../../../components/Stars';
import { ServerErrorState } from '../../../components/system-states/SystemState';
import { ModuleGate } from '../../../components/ModuleGate';
import { useRankingDetail } from '../../ranking/api/ranking-api';
import { ProfileBreakdownView } from '../../ranking/components/ProfileBreakdownView';
import type { RejectedCard } from '../rejected-lane';
import styles from './ReviewPreview.module.css';

export type ReviewDecision = ReviewStatus | 'rejected';

/** Who the preview shows: someone in a review lane, or someone in Rejected (F23.18). */
export interface PreviewPerson {
  readonly applicationId: string;
  readonly name: string;
  /** Null for someone in Rejected. */
  readonly reviewStatus: ReviewStatus | null;
  readonly subLine: string;
  /** From the board's row; for someone in Rejected these come from their score sheet. */
  readonly score: number | null;
  readonly stars: number | null;
  readonly missingMustHaves: readonly string[];
  /** Set for someone in Rejected: why they were rejected. */
  readonly rejection: { readonly reasonLabel: string | null } | null;
  /** ADR 0062: the profile's reasons for this person, worked out in the browser; null for a job still ranked by the criteria, or someone in Rejected. */
  readonly profile: ProfileBreakdown | null;
}

export function previewOfRow(row: RankingRow, profile: ProfileBreakdown | null): PreviewPerson {
  return {
    applicationId: row.applicationId,
    name: row.name,
    reviewStatus: row.reviewStatus ?? 'to_review',
    subLine: subLineOf(row),
    score: row.score,
    stars: row.stars,
    missingMustHaves: row.missingMustHaves,
    rejection: null,
    profile,
  };
}

export function previewOfRejected(card: RejectedCard): PreviewPerson {
  return { applicationId: card.applicationId, name: card.name, reviewStatus: null, subLine: 'Rejected', score: null, stars: null, missingMustHaves: [], rejection: { reasonLabel: card.reasonLabel }, profile: null };
}

interface ReviewPreviewProps {
  readonly person: PreviewPerson;
  /** Null for someone who may not decide: the buttons are left out, not greyed. */
  readonly decide: {
    /** Why this person can't be moved from here, in the server's words, or null. */
    readonly refusal: string | null;
    readonly isPending: boolean;
    readonly onDecide: (to: ReviewDecision, trigger: HTMLElement) => void;
  } | null;
  /** The full "Why this score" view: the whole CV, and ✓/✗ corrections. */
  readonly onOpenFull: () => void;
}

/** The review card's keys (SCR-01), so the board and "Start review" answer the same keys. */
const DECISION_KEYS: Readonly<Record<string, ReviewDecision>> = { a: 'shortlisted', h: 'maybe', r: 'rejected' };
/** The CV lines shown here; the full view has the rest. */
const CV_LINES_SHOWN = 3;

/**
 * Stage ④'s preview (Review board Idea B, DECISIONS-LOG 2026-10-25): the person picked on the
 * board, beside it — who they are, each screening criterion met or not, the CV lines that
 * decided them, and the decision. Deciding here is the board's own move, so the lanes update at
 * once and the next person in line takes this place.
 */
export function ReviewPreview({ person, decide, onOpenFull }: ReviewPreviewProps): ReactNode {
  const detail = useRankingDetail(person.applicationId);
  const isRejected = person.rejection !== null;
  const score = person.score ?? detail.data?.score ?? null;
  const stars = person.stars ?? detail.data?.stars ?? null;
  const missing = isRejected ? (detail.data?.missingMustHaves ?? []) : person.missingMustHaves;
  const buttons = useRef<Partial<Record<ReviewDecision, HTMLButtonElement | null>>>({});
  const canDecide = decide !== null && decide.refusal === null;
  const refFor =
    (to: ReviewDecision) =>
    (element: HTMLButtonElement | null): void => {
      buttons.current[to] = element;
    };

  useEffect(() => {
    if (!canDecide) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.ctrlKey || event.metaKey || event.altKey || isTyping(event.target)) return;
      // A pop-up or the full view has the keyboard while it is open.
      if (document.querySelector('[data-popover], [aria-modal="true"]')) return;
      const to = DECISION_KEYS[event.key.toLowerCase()];
      const trigger = to ? buttons.current[to] : null;
      if (!to || !trigger) return;
      event.preventDefault();
      decide.onDecide(to, trigger);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [canDecide, decide]);

  return (
    <aside className={styles.preview} aria-label={`Preview of ${person.name}`}>
      <header className={styles.top}>
        <span className={styles.initials} aria-hidden>
          {initialsOf(person.name)}
        </span>
        <div className={styles.who}>
          <Link to="/applications/$applicationId" params={{ applicationId: person.applicationId }} className={styles.name}>
            {person.name || '—'}
          </Link>
          <p className={styles.sub}>{person.subLine}</p>
        </div>
        <span className={styles.score} title={person.profile ? 'Match with the ideal candidate' : 'ATS score'}>
          {score ?? '—'}
        </span>
      </header>

      <div className={styles.body}>
        <div className={styles.stars}>
          <Stars value={stars} />
          {missing.length > 0 && <Chip tone="rejected">Missing: {missing.join(', ')}</Chip>}
          {person.rejection && <Chip tone="rejected">Rejected{person.rejection.reasonLabel ? `: ${person.rejection.reasonLabel}` : ''}</Chip>}
        </div>
        {detail.isPending && <SkeletonLines count={6} />}
        {detail.error && <ServerErrorState onRetry={() => void detail.refetch()} />}
        {detail.data && (
          <>
            {person.profile && <ProfileBreakdownView breakdown={person.profile} />}
            {/* The criteria's own marks: the ranking for a job still ranked by them, the lens beside the profile when that module is on. */}
            {person.profile ? (
              <ModuleGate module="screening_criteria">
                <Criteria matches={detail.data.matches} />
              </ModuleGate>
            ) : (
              <Criteria matches={detail.data.matches} />
            )}
            <FromTheCv detail={detail.data} onOpenFull={onOpenFull} />
          </>
        )}
      </div>

      {decide && (
        <footer className={styles.decide}>
          {decide.refusal !== null ? (
            <p className={styles.refusal}>{decide.refusal}</p>
          ) : isRejected ? (
            <>
              {/* F23.18: back into review, in the lane chosen. */}
              <Button variant="outline" icon={Undo2} disabled={decide.isPending} onClick={(event) => decide.onDecide('to_review', event.currentTarget)}>
                Back to review
              </Button>
              <DecisionButtons person={person} decide={decide} refFor={refFor} />
            </>
          ) : (
            <>
              <DecisionButtons person={person} decide={decide} refFor={refFor} />
              <Button
                ref={refFor('rejected')}
                variant="danger"
                icon={X}
                disabled={decide.isPending}
                onClick={(event) => decide.onDecide('rejected', event.currentTarget)}
              >
                Reject <Kbd>R</Kbd>
              </Button>
            </>
          )}
        </footer>
      )}
    </aside>
  );
}

/** Shortlist (A) and Maybe (H), each left out where the person already is. */
function DecisionButtons({
  person,
  decide,
  refFor,
}: {
  readonly person: PreviewPerson;
  readonly decide: NonNullable<ReviewPreviewProps['decide']>;
  readonly refFor: (to: ReviewDecision) => (element: HTMLButtonElement | null) => void;
}): ReactNode {
  return (
    <>
      {person.reviewStatus !== 'shortlisted' && (
        <Button
          ref={refFor('shortlisted')}
          variant="solid"
          icon={ListChecks}
          disabled={decide.isPending}
          onClick={(event) => decide.onDecide('shortlisted', event.currentTarget)}
        >
          Shortlist <Kbd inverse>A</Kbd>
        </Button>
      )}
      {person.reviewStatus !== 'maybe' && (
        <Button
          ref={refFor('maybe')}
          variant="outline"
          icon={PauseCircle}
          disabled={decide.isPending}
          onClick={(event) => decide.onDecide('maybe', event.currentTarget)}
        >
          Maybe <Kbd>H</Kbd>
        </Button>
      )}
    </>
  );
}

/** Each screening criterion on one line: its name, and met or not. The reason is its tooltip. */
function Criteria({ matches }: { readonly matches: readonly RankedCriterion[] }): ReactNode {
  if (matches.length === 0) return null;
  return (
    <section className={styles.section} aria-label="Screening criteria">
      <h3 className={styles.heading}>Screening criteria</h3>
      <ul className={styles.criteria}>
        {matches.map((match) => (
          <li key={match.key} className={styles.criterion} title={match.reason}>
            <span className={styles.criterionLabel}>{match.label}</span>
            <span className={styles.mark} data-mark={match.mark}>
              {CRITERION_MARK_SYMBOLS[match.mark]} {CRITERION_MARK_LABELS[match.mark]}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The CV lines that decided a criterion, their words marked, and the way into the whole CV. */
function FromTheCv({ detail, onOpenFull }: { readonly detail: RankingDetail; readonly onOpenFull: () => void }): ReactNode {
  const lines = detail.matches.flatMap((match) => (match.source === 'cv' && match.evidence ? [{ key: match.key, ...match.evidence }] : [])).slice(0, CV_LINES_SHOWN);
  return (
    <section className={styles.section} aria-label="From the CV">
      <h3 className={styles.heading}>From the CV</h3>
      <div className={styles.cvLines}>
        {lines.length > 0 ? (
          lines.map((line) => (
            <p key={line.key}>
              {line.text.slice(0, line.start)}
              <mark>{line.text.slice(line.start, line.end)}</mark>
              {line.text.slice(line.end)}
            </p>
          ))
        ) : (
          <p>{cvNoteOf(detail)}</p>
        )}
      </div>
      {detail.cv.documentId !== null && (
        <Button size="sm" variant="outline" onClick={onOpenFull}>
          Open the full CV
        </Button>
      )}
    </section>
  );
}

function cvNoteOf(detail: RankingDetail): string {
  if (detail.cv.state === 'unreadable') return CV_SCAN_MESSAGE;
  if (detail.cv.state === 'coming') return 'The CV is still on its way.';
  if (detail.cv.documentId === null) return 'No CV.';
  return 'Nothing in the CV decided a criterion.';
}

const MONTHS_PER_YEAR = 12;

/** What the board knows about them at a glance: experience, city, education and source. */
function subLineOf(row: RankingRow): string {
  const parts = [
    row.experienceMonths === null ? null : `${(row.experienceMonths / MONTHS_PER_YEAR).toFixed(1)} yrs`,
    row.city,
    row.education === null ? null : EDUCATION_LEVEL_LABELS[row.education],
    row.source,
  ].filter((part): part is string => part !== null && part !== '');
  return parts.length > 0 ? parts.join(' · ') : '—';
}

function initialsOf(name: string): string {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
  return letters.slice(0, 2) || '—';
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}
